import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setRefreshToken,
  updateAccessToken,
  clearSessionStorage,
  isAuthEndpoint,
} from './auth';
import { isAccessTokenExpired, isRefreshTokenExpired } from './tokenClaims';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

export async function logout() {
  try {
    await api.post('/user/logout', {});
  } catch {
    /* proceed with local cleanup even if logout API fails */
  } finally {
    clearSessionStorage();
  }
}

let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

function redirectToLogin() {
  clearSessionStorage();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

function clearAuthHeader(config) {
  if (!config?.headers) return;
  delete config.headers.Authorization;
  delete config.headers.authorization;
}

/** 401 always; 403 only when the failure looks like a bad/expired access JWT. */
function shouldAttemptTokenRefresh(error) {
  const status = error.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  const message = String(error.response?.data?.message ?? '').toLowerCase();
  return (
    message.includes('token verification failed') ||
    message.includes('jwt') ||
    message.includes('invalid token') ||
    message.includes('access token') ||
    message.includes('authorization')
  );
}

async function performTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  if (isRefreshTokenExpired(refreshToken)) {
    throw new Error('Refresh token expired');
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const refreshResponse = await api.post('/user/refresh-token', {
      refreshToken,
    });
    const newToken =
      refreshResponse.data?.accessToken || refreshResponse.data?.token || '';
    if (!newToken) {
      throw new Error('Refresh response missing access token');
    }
    updateAccessToken(newToken);
    const newRefresh = refreshResponse.data?.refreshToken;
    if (newRefresh) {
      setRefreshToken(newRefresh);
    }
    processRefreshQueue(null, newToken);
    return newToken;
  } catch (refreshError) {
    processRefreshQueue(refreshError, null);
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

async function ensureValidAccessToken() {
  const accessToken = getAccessToken();
  if (accessToken && !isAccessTokenExpired(accessToken)) {
    return accessToken;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    if (accessToken) return accessToken;
    throw new Error('Not authenticated');
  }

  return performTokenRefresh();
}

/** Refresh access token using the stored refresh token (e.g. on app init). */
export async function refreshSessionTokens() {
  return performTokenRefresh();
}

api.interceptors.request.use(
  async (config) => {
    const url = config.url ?? '';
    if (isAuthEndpoint(url)) {
      return config;
    }

    const onLoginPage =
      typeof window !== 'undefined' && window.location.pathname === '/login';
    if (onLoginPage) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        clearAuthHeader(config);
      }
    } catch {
      /* refresh failed — never send a stale access token */
      clearAuthHeader(config);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';
    const status = error.response?.status;

    const onLoginPage =
      typeof window !== 'undefined' && window.location.pathname === '/login';

    if (
      status === 403 &&
      !isAuthEndpoint(url) &&
      !shouldAttemptTokenRefresh(error)
    ) {
      error.isAccessDenied = true;
      return Promise.reject(error);
    }

    if (
      shouldAttemptTokenRefresh(error) &&
      !isAuthEndpoint(url) &&
      !onLoginPage &&
      originalRequest &&
      !originalRequest._retry
    ) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const newToken = await performTokenRefresh();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export const createSOP = (data) => api.post('/sop/create', data);
export const getSOPTypes = () => api.get('/sop/types');
export const getSOPByType = (type) => api.get(`/sop/${type}`);
export const submitEmployeeSOP = (data) => api.post('/sop/employee/submit', data);
